import { T } from "../tokens";
import { usePulse } from "../hooks/useMotion";
import type { CSSProperties, ReactNode } from "react";

type Variant = "teal" | "ok" | "warn" | "err" | "info" | "default" | "ghost";
type Size    = "sm" | "md" | "lg";

const variants: Record<Variant, { bg: string; color: string; border: string }> = {
  teal:    { bg: T.tealBg,     color: T.tealSoft, border: T.tealBdr },
  ok:      { bg: T.okBg,       color: T.ok,        border: T.okBdr },
  warn:    { bg: T.warnBg,     color: T.warn,      border: T.warnBdr },
  err:     { bg: T.errBg,      color: T.err,        border: T.errBdr },
  info:    { bg: T.infoBg,    color: T.info,       border: T.infoBdr },
  default: { bg: T.s3,         color: T.t1,         border: T.b1 },
  ghost:   { bg: "transparent", color: T.t2,        border: T.b1 },
};

const sizes: Record<Size, { pad: string; fs: string }> = {
  sm: { pad: "2px 7px",  fs: "9px" },
  md: { pad: "3px 9px",  fs: "10px" },
  lg: { pad: "5px 12px", fs: "11px" },
};

interface Props {
  variant?:  Variant;
  size?:     Size;
  children:  ReactNode;
  style?:    CSSProperties;
  onClick?:  () => void;
  pulse?:    boolean;
}

export function Badge({ variant = "default", size = "md", children, style, onClick, pulse }: Props) {
  const v = variants[variant];
  const s = sizes[size];
  const p = usePulse(1800);

  return (
    <span
      onClick={onClick}
      role={onClick ? "button" : undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        background: v.bg, color: v.color, border: `1px solid ${v.border}`,
        borderRadius: "99px", padding: s.pad, fontSize: s.fs,
        fontFamily: T.mono, fontWeight: 500,
        letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {pulse && (
        <span style={{
          width: 5, height: 5, borderRadius: "50%", background: v.color, flexShrink: 0,
          opacity: 0.4 + 0.6 * p, transform: `scale(${0.85 + 0.3 * p})`,
          transition: "opacity 0.1s linear, transform 0.1s linear",
        }}/>
      )}
      {children}
    </span>
  );
}
